import { Fragment, ReactNode, useRef } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import Draggable from 'react-draggable';
import { cn } from '@/utils/uiUtils';

type DialogProps = {
  show: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  dismissible?: boolean;
  unmount?: boolean;
};

type DialogHeaderProps = {
  children: ReactNode;
  className?: string;
};

type DialogBodyProps = {
  children: ReactNode;
  className?: string;
};

type DialogFooterProps = {
  children: ReactNode;
  className?: string;
};

export function Dialog({
  show,
  onClose,
  children,
  className,
  dismissible = true,
  unmount = true,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  return (
    <Transition show={show} as={Fragment} unmount={unmount}>
      <HeadlessDialog
        as='div'
        className='relative z-50'
        onClose={dismissible ? onClose : () => {}}
        unmount={unmount}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
          unmount={unmount}
        >
          <div
            className='fixed inset-0 bg-black/30 dark:bg-black/50'
            aria-hidden='true'
          />
        </Transition.Child>
        {/* Removed the intermediate positioning div */}
        <Transition.Child
          as='div'
          className='fixed inset-0 z-[9999] flex items-center justify-center p-4'
          enter='ease-out duration-300'
          enterFrom='opacity-0 scale-95'
          enterTo='opacity-100 scale-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100 scale-100'
          leaveTo='opacity-0 scale-95'
          unmount={unmount}
        >
          <Draggable
            bounds='parent'
            handle='.dialog-draggable-handle'
            nodeRef={panelRef}
          >
            <HeadlessDialog.Panel
              ref={panelRef}
              className={cn(
                'w-[75vw] max-w-[75vw] md:max-w-[800px] max-h-[calc(100vh-2rem)] transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all flex flex-col',
                className
              )}
            >
              {children}
            </HeadlessDialog.Panel>
          </Draggable>
        </Transition.Child>
      </HeadlessDialog>
    </Transition>
  );
}

Dialog.Header = function DialogHeader({
  children,
  className,
}: DialogHeaderProps) {
  return (
    <div
      className={cn(
        'dialog-draggable-handle cursor-move select-none px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 relative',
        className
      )}
    >
      {children}
    </div>
  );
};

Dialog.Body = function DialogBody({ children, className }: DialogBodyProps) {
  return (
    <div
      className={cn(
        'flex-1 min-h-0 overflow-y-auto px-4 py-4 text-gray-700 dark:text-gray-100',
        className
      )}
    >
      {children}
    </div>
  );
};

Dialog.Footer = function DialogFooter({
  children,
  className,
}: DialogFooterProps) {
  return (
    <div
      className={cn(
        'px-4 py-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-end gap-2',
        className
      )}
    >
      {children}
    </div>
  );
};

Dialog.Title = HeadlessDialog.Title;
Dialog.Description = HeadlessDialog.Description;
